import useReportSort from '../../components/reports/useReportSort';
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiDownload,
  FiFileText,
  FiFilter,
  FiLoader,
  FiRefreshCw,
  FiSearch,
  FiShoppingBag,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
import ReportFilterModal from "../../components/reports/ReportFilterModal";
import SearchableSelect from "../../components/reports/SearchableSelect";
const money = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const localDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function save(blob, name) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}
export default function PurchaseSaleWiseReportPage() {
  const { accessToken, user } = useAuth();
  const date = new Date();
  const initialFilters = {
    from: localDate(new Date(date.getFullYear(), date.getMonth(), 1)),
    to: localDate(date),
    storeId: "",
    search: "",
  };
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [stores, setStores] = useState([]);
  const [rawReport, setReport] = useState(null);
  const { report, controls: sortControls } = useReportSort(rawReport);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const canGenerate = hasPermission(
    user,
    "reports.purchase_sale_wise.generate",
  );
  const canExport = hasPermission(user, "reports.purchase_sale_wise.export");
  const loadStores = useCallback(async () => {
    try {
      setStores(
        (await apiRequest("/business-reports/purchase-sale-wise/stores", {
          accessToken,
        })) || [],
      );
    } catch (requestError) {
      setError(requestError.message || "Could not load report stores.");
    }
  }, [accessToken]);
  useEffect(() => {
    if (accessToken) loadStores();
  }, [accessToken, loadStores]);
  const visibleRows = useMemo(() => {
    const query = appliedFilters.search.trim().toLowerCase();
    if (!query) return report?.rows || [];
    return (report?.rows || []).filter((row) =>
      [
        row.product,
        row.productCode,
        row.sku,
        row.variant,
        row.color,
        row.size,
      ].some((value) => String(value || "").toLowerCase().includes(query)),
    );
  }, [appliedFilters.search, report]);
  const filteredSummary = useMemo(
    () =>
      visibleRows.reduce(
        (summary, row) => ({
          variants: summary.variants + 1,
          purchasedQty: summary.purchasedQty + Number(row.purchasedQty || 0),
          purchaseValue: summary.purchaseValue + Number(row.purchaseValue || 0),
          soldQty: summary.soldQty + Number(row.soldQty || 0),
          salesValue: summary.salesValue + Number(row.salesValue || 0),
          returnQty: summary.returnQty + Number(row.returnQty || 0),
          available: summary.available + Number(row.available || 0),
        }),
        { variants: 0, purchasedQty: 0, purchaseValue: 0, soldQty: 0, salesValue: 0, returnQty: 0, available: 0 },
      ),
    [visibleRows],
  );
  const generate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setError("");
    try {
      setReport(
        await apiRequest("/business-reports/purchase-sale-wise/generate", {
          method: "POST",
          body: {
            from: draftFilters.from,
            to: draftFilters.to,
            ...(draftFilters.storeId ? { storeId: draftFilters.storeId } : {}),
          },
          accessToken,
        }),
      );
      setAppliedFilters({ ...draftFilters });
      setShowFilters(false);
    } catch (e) {
      setError(e.message || "Could not generate this report.");
    } finally {
      setLoading(false);
    }
  };
  const exportExcel = async () => {
    if (!report || !canExport) return;
    setExporting(true);
    try {
      const response = await apiRequest(
        `/business-reports/purchase-sale-wise/export?${new URLSearchParams({
          from: appliedFilters.from,
          to: appliedFilters.to,
          ...(appliedFilters.storeId ? { storeId: appliedFilters.storeId } : {}),
          format: "XLSX",
        })}`,
        { accessToken, rawResponse: true },
      );
      save(await response.blob(), "charms-purchase-sale.xlsx");
    } catch (e) {
      setError(e.message || "Could not export this report.");
    } finally {
      setExporting(false);
    }
  };
  return (
    <div className="business-report space-y-6">
      <section className="border-b border-slate-200 bg-white px-1 pb-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[.18em] text-primary-800">
              <FiShoppingBag /> Business reports
            </div>
            <h1 className="text-3xl font-bold text-slate-950">
              Purchase and Sale-wise Report
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-500">
              Compare each variant’s posted GRN receipts, POS sales, returns,
              and current available stock.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => { setDraftFilters({ ...appliedFilters }); setShowFilters(true); }} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:border-primary-300 hover:text-primary-800">
              <FiFilter /> Filters
            </button>
            <button onClick={generate} disabled={!canGenerate || loading} className="inline-flex items-center gap-2 rounded-lg bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-900 disabled:opacity-50">
              {loading ? <FiLoader className="animate-spin" /> : <FiRefreshCw />}
              {loading ? "Generating…" : "Generate"}
            </button>
            <button onClick={exportExcel} disabled={!report || !canExport || exporting} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:border-primary-300 hover:text-primary-800 disabled:opacity-50">
              {exporting ? <FiLoader className="animate-spin" /> : <FiDownload />}
              Export Excel
            </button>
          </div>
        </div>
      </section>
      {error && (
        <p className="rounded-xl bg-red-50 p-4 text-sm text-red-700">{error}</p>
      )}
      {!report ? (
        <section className="rounded-3xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
          <FiFileText className="mx-auto mb-4 text-4xl text-primary-300" />
          <h2 className="text-xl font-bold">No preview generated yet</h2>
        </section>
      ) : (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {[
              ["Variants", filteredSummary.variants],
              ["Purchased", filteredSummary.purchasedQty],
              ["Purchase value", money(filteredSummary.purchaseValue)],
              ["Sales value", money(filteredSummary.salesValue)],
              ["Available", filteredSummary.available],
            ].map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
              >
                <p className="text-sm text-gray-500">{label}</p>
                <p className="mt-2 text-2xl font-bold text-primary-800">
                  {typeof value === "number"
                    ? Number(value).toLocaleString("en-IN")
                    : value}
                </p>
              </div>
            ))}
          </section>
          <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
            <div className="flex flex-col gap-2 border-b border-slate-100 p-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-950">Variant movement comparison</h2>
                <p className="mt-1 text-sm text-slate-500">{visibleRows.length.toLocaleString("en-IN")} variant(s) · {appliedFilters.from} to {appliedFilters.to}</p>
              </div>
            </div>
            <div className="overflow-x-auto">
                  {sortControls}
              <table className="min-w-[1450px] w-full text-sm">
                <thead className="bg-primary-800 text-left text-xs font-bold uppercase tracking-wide text-white">
                  <tr>
                    {[
                      "Product / Article",
                      "SKU / Variant",
                      "Colour / Size",
                      "Purchased qty",
                      "Purchase value",
                      "Sold qty",
                      "Sales value",
                      "Returned qty",
                      "On hand",
                      "Reserved",
                      "Available",
                    ].map((h) => (
                      <th key={h} className="px-4 py-3">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr
                      key={row.variantId}
                      className="border-t border-slate-200 text-slate-700 hover:bg-primary-50/30"
                    >
                      <td className="px-4 py-3 font-medium">
                        {row.product}
                        <p className="mt-1 text-xs text-gray-500">
                          {row.productCode || "—"}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        {row.sku}
                        <p className="mt-1 text-xs text-gray-500">
                          {row.variant || "Default"}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        {row.color || "—"} / {row.size || "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {row.purchasedQty}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {money(row.purchaseValue)}
                      </td>
                      <td className="px-4 py-3 text-right">{row.soldQty}</td>
                      <td className="px-4 py-3 text-right">
                        {money(row.salesValue)}
                      </td>
                      <td className="px-4 py-3 text-right">{row.returnQty}</td>
                      <td className="px-4 py-3 text-right">{row.onHand}</td>
                      <td className="px-4 py-3 text-right">{row.reserved}</td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {row.available}
                      </td>
                    </tr>
                  ))}
                  {!visibleRows.length && <tr><td colSpan="11" className="px-4 py-16 text-center text-slate-500">No variants match the selected filters.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
      <ReportFilterModal open={showFilters} title="Purchase and sale-wise filters" description="Choose the period, godown, and optional product search. Generate applies the filters and closes this window." loading={loading} onApply={generate} onClose={() => { setDraftFilters({ ...appliedFilters }); setShowFilters(false); }}>
        <div className="grid gap-4 sm:grid-cols-2">
          <FilterField label="From date"><input type="date" value={draftFilters.from} onChange={(event) => setDraftFilters((value) => ({ ...value, from: event.target.value }))} className="report-input" /></FilterField>
          <FilterField label="To date"><input type="date" value={draftFilters.to} onChange={(event) => setDraftFilters((value) => ({ ...value, to: event.target.value }))} className="report-input" /></FilterField>
          <FilterField label="Godown / Store"><SearchableSelect value={draftFilters.storeId} onChange={(value) => setDraftFilters((current) => ({ ...current, storeId: value }))} placeholder="All stores" options={stores.map((store) => ({ value: store.id, label: `${store.name} (${store.code})` }))} /></FilterField>
          <FilterField label="Search product, article, SKU or variant"><div className="relative"><FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input value={draftFilters.search} onChange={(event) => setDraftFilters((value) => ({ ...value, search: event.target.value }))} className="report-input pl-9" placeholder="Product, article no., SKU..." /></div></FilterField>
        </div>
      </ReportFilterModal>
    </div>
  );
}

function FilterField({ label, children }) {
  return <label className="block text-sm font-semibold text-slate-700"><span className="mb-2 block">{label}</span>{children}</label>;
}
